import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api, mediaSrcById } from "../api";
import { buddyEnabled } from "../lib/buddyPrefs";
import { StoreVersionsCard, UserAppVersion } from "../components/StoreVersionsCard";
import { ForceUpdateCard } from "../components/ForceUpdateCard";
import { BroadcastPanel } from "../components/BroadcastPanel";
import { InvitesPanel } from "../components/InvitesPanel";
import { useApp } from "../context/AppContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "../components/ui/select";
import { InviteDialog, looksLikeIdentifier } from "../components/InviteDialog";
import { toast } from "sonner";
import {
  ShieldCheck, ArrowLeft, RotateCcw, Fingerprint, Smartphone, Trash2, Send,
  UserPlus, RefreshCw, Users as UsersIcon, Bell, Database, Apple, Download,
  FolderSync, CheckCircle2, AlertTriangle, FileText, Briefcase, Pencil, MessageSquare, Zap, Bot,
  UserCheck, UserX, MailQuestion, Building2, Languages, Loader2, Clock3, ChevronDown, Link2,
  Globe,
} from "lucide-react";
import { CompanyCardsTab } from "../components/CompanyCardsTab";
import { AdminConversationsTab } from "../components/AdminConversationsTab";
import { Switch } from "../components/ui/switch";
import { Progress } from "../components/ui/progress";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

const ROLES = ["installer", "supervisor", "office", "manager", "admin"];

function EditUserDialog({ user: target, crews, onClose, onDone, t }) {
  const [form, setForm] = useState({ name: "", email: "", role: "installer", language: "en", crew_id: "", password: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (target) {
      setForm({
        name: target.name || "",
        email: target.email || "",
        role: target.role || "installer",
        language: target.language || "en",
        crew_id: target.crew_id || "",
        password: "",
      });
    }
  }, [target]);
  const submit = async () => {
    if (form.password && form.password.length < 6) { toast.error(t("password_min")); return; }
    setBusy(true);
    try {
      await api.patch(`/admin/users/${target.id}`, {
        name: form.name,
        email: form.email,
        role: form.role,
        language: form.language,
        crew_id: form.crew_id || "",
      });
      if (form.password) {
        await api.post(`/admin/users/${target.id}/reset-password`, { new_password: form.password });
      }
      toast.success(t("saved"));
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("something_wrong"));
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="admin-edit-user-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Pencil className="h-5 w-5 text-primary" /> {t("edit_user")}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-muted-foreground">{t("full_name")}</label>
            <Input data-testid="admin-eu-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">Email</label>
            <Input data-testid="admin-eu-email" type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-semibold text-muted-foreground">{t("role")}</label>
              <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                <SelectTrigger data-testid="admin-eu-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => <SelectItem key={r} value={r}>{t(r)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground">{t("language_label")}</label>
              <Select value={form.language} onValueChange={(v) => setForm((f) => ({ ...f, language: v }))}>
                <SelectTrigger data-testid="admin-eu-language"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="pt">Português</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">{t("crew")}</label>
            <Select value={form.crew_id || "none"} onValueChange={(v) => setForm((f) => ({ ...f, crew_id: v === "none" ? "" : v }))}>
              <SelectTrigger data-testid="admin-eu-crew"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t("no_crew")}</SelectItem>
                {crews.map((c) => <SelectItem key={c.id} value={c.id}>{c.code}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">{t("new_password_optional")}</label>
            <Input
              data-testid="admin-eu-password"
              type="text"
              placeholder={t("keep_current_password")}
              value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
            />
          </div>
          <Button data-testid="admin-eu-submit" className="w-full" onClick={submit} disabled={busy || !form.name || !form.email}>
            {busy ? "..." : t("save")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CreateUserDialog({ open, onClose, onDone, t }) {
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "installer", language: "en" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/admin/users", form);
      toast.success(t("user_created"));
      setForm({ name: "", email: "", password: "", role: "installer", language: "en" });
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("something_wrong"));
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="admin-create-user-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-primary" /> {t("create_user")}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Input data-testid="admin-cu-name" placeholder={t("full_name")} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          <Input data-testid="admin-cu-email" type="email" placeholder="Email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
          <Input data-testid="admin-cu-password" type="text" placeholder={t("password")} value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} />
          <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
            <SelectTrigger data-testid="admin-cu-role"><SelectValue /></SelectTrigger>
            <SelectContent>
              {ROLES.map((r) => <SelectItem key={r} value={r}>{t(r)}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button data-testid="admin-cu-submit" className="w-full" onClick={submit} disabled={busy || !form.name || !form.email || form.password.length < 6}>
            {busy ? "..." : t("create_user")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Admin-only: quick reply phrases shown by the ⚡ button in the composer. */
function QuickRepliesTab({ t }) {
  const [list, setList] = useState(null);
  const [newText, setNewText] = useState("");
  const [editing, setEditing] = useState(null); // {id, text}
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/quick-replies")
      .then((r) => setList(r.data || []))
      .catch((e) => { toast.error(e?.response?.data?.detail || "Error"); setList([]); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    const text = newText.trim();
    if (!text) return;
    setBusy(true);
    try {
      await api.post("/admin/quick-replies", { text });
      setNewText("");
      toast.success(t("quick_reply_added"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("something_wrong"));
    } finally { setBusy(false); }
  };

  const save = async () => {
    if (!editing || !editing.text.trim()) return;
    setBusy(true);
    try {
      await api.patch(`/admin/quick-replies/${editing.id}`, { text: editing.text.trim() });
      setEditing(null);
      toast.success(t("quick_reply_saved"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("something_wrong"));
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/quick-replies/${id}`);
      toast.success(t("quick_reply_deleted"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("something_wrong"));
    }
  };

  const move = async (idx, dir) => {
    // Reorder by swapping the `order` value with the neighbour
    const items = list || [];
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const a = items[idx];
    const b = items[j];
    try {
      await Promise.all([
        api.patch(`/admin/quick-replies/${a.id}`, { order: j + 1 }),
        api.patch(`/admin/quick-replies/${b.id}`, { order: idx + 1 }),
      ]);
      load();
    } catch (e) {
      toast.error(t("something_wrong"));
    }
  };

  return (
    <div className="space-y-3" data-testid="admin-quick-replies-tab">
      <div className="flex items-center gap-2">
        <Input
          data-testid="admin-qr-new-input"
          placeholder={t("quick_reply_placeholder")}
          value={newText}
          onChange={(e) => setNewText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") add(); }}
          className="flex-1"
          maxLength={200}
        />
        <Button data-testid="admin-qr-add-btn" onClick={add} disabled={busy || !newText.trim()}>
          <Zap className="h-4 w-4 mr-1.5" /> {t("quick_reply_add")}
        </Button>
      </div>
      {list === null && <div className="text-sm text-muted-foreground py-8 text-center">…</div>}
      {list !== null && list.length === 0 && (
        <div className="text-sm text-muted-foreground py-8 text-center">{t("quick_replies_empty")}</div>
      )}
      {(list || []).map((qr, idx) => (
        <Card key={qr.id} className="p-3 flex items-center gap-2" data-testid="admin-qr-row">
          <div className="flex flex-col shrink-0">
            <button
              type="button"
              data-testid="admin-qr-move-up"
              onClick={() => move(idx, -1)}
              disabled={idx === 0}
              className="h-5 w-6 flex items-center justify-center rounded hover:bg-muted disabled:opacity-25 text-muted-foreground"
            >▲</button>
            <button
              type="button"
              data-testid="admin-qr-move-down"
              onClick={() => move(idx, 1)}
              disabled={idx === (list || []).length - 1}
              className="h-5 w-6 flex items-center justify-center rounded hover:bg-muted disabled:opacity-25 text-muted-foreground"
            >▼</button>
          </div>
          {editing?.id === qr.id ? (
            <>
              <Input
                data-testid="admin-qr-edit-input"
                value={editing.text}
                onChange={(e) => setEditing((prev) => ({ ...prev, text: e.target.value }))}
                onKeyDown={(e) => { if (e.key === "Enter") save(); }}
                className="flex-1 h-9"
                maxLength={200}
                autoFocus
              />
              <Button size="sm" className="h-9" data-testid="admin-qr-save-btn" onClick={save} disabled={busy}>OK</Button>
              <Button size="sm" variant="ghost" className="h-9" onClick={() => setEditing(null)}>✕</Button>
            </>
          ) : (
            <>
              <span className="flex-1 text-sm font-medium min-w-0 truncate">{qr.text}</span>
              <Button size="sm" variant="outline" className="h-8" data-testid="admin-qr-edit-btn" onClick={() => setEditing({ id: qr.id, text: qr.text })}>
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              <Button size="sm" variant="outline" className="h-8 text-destructive hover:text-destructive" data-testid="admin-qr-delete-btn" onClick={() => remove(qr.id)}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </>
          )}
        </Card>
      ))}
    </div>
  );
}

/** Admin-only: Buddy configuration — company rules (global or per-user) + memory management. */
function BuddyAdminTab({ t, users }) {
  const { user } = useApp();
  const [rules, setRules] = useState(null);
  const [newRule, setNewRule] = useState("");
  const [newTarget, setNewTarget] = useState("all");
  const [busy, setBusy] = useState(false);
  const [editingRule, setEditingRule] = useState(null); // {id, text, target_user_id}
  const [memUser, setMemUser] = useState("");
  const [mems, setMems] = useState(null);
  const [newMem, setNewMem] = useState("");
  const [editingMem, setEditingMem] = useState(null); // {id, text}
  // interpreter (client mode): company knowledge + introduction instructions
  const [knowledge, setKnowledge] = useState(null);
  const [newKnow, setNewKnow] = useState("");
  const [newKnowKind, setNewKnowKind] = useState("term");
  const [introInstr, setIntroInstr] = useState("");
  const [askHelpers, setAskHelpers] = useState(false);
  const [introBusy, setIntroBusy] = useState(false);

  // MASTER: platform-wide knowledge (every company + solo users)
  const isMaster = !!user?.super_admin;
  const [globalKnow, setGlobalKnow] = useState(null);
  const [newGlobal, setNewGlobal] = useState("");
  const [newGlobalKind, setNewGlobalKind] = useState("term");

  const loadKnowledge = useCallback(() => {
    if (!user?.id) return;
    api.get("/buddy/knowledge", { params: { user_id: user.id } })
      .then((r) => setKnowledge(r.data?.items || [])).catch(() => setKnowledge([]));
    if (user?.super_admin) {
      api.get("/buddy/knowledge", { params: { user_id: user.id, scope: "global" } })
        .then((r) => setGlobalKnow(r.data?.items || [])).catch(() => setGlobalKnow([]));
    }
    api.get("/interpreter/settings", { params: { user_id: user.id } })
      .then((r) => { setIntroInstr(r.data?.intro_instructions || ""); setAskHelpers(!!r.data?.ask_helpers); }).catch(() => {});
  }, [user?.id]);
  useEffect(() => { loadKnowledge(); }, [loadKnowledge]);

  const addKnowledge = async () => {
    const text = newKnow.trim();
    if (!text) return;
    setBusy(true);
    try {
      await api.post("/buddy/knowledge", { user_id: user.id, kind: newKnowKind, text });
      setNewKnow("");
      toast.success(t("interp_know_added"));
      loadKnowledge();
    } catch (e) { toast.error(t("something_wrong")); } finally { setBusy(false); }
  };

  const removeKnowledge = async (id) => {
    try {
      await api.delete(`/buddy/knowledge/${id}`, { params: { user_id: user.id } });
      loadKnowledge();
    } catch (e) { toast.error(t("something_wrong")); }
  };

  const addGlobalKnowledge = async () => {
    const text = newGlobal.trim();
    if (!text) return;
    setBusy(true);
    try {
      await api.post("/buddy/knowledge", { user_id: user.id, kind: newGlobalKind, text, scope: "global" });
      setNewGlobal("");
      toast.success(t("buddy_global_added"));
      loadKnowledge();
    } catch (e) { toast.error(t("something_wrong")); } finally { setBusy(false); }
  };

  const saveIntroInstr = async () => {
    setIntroBusy(true);
    try {
      await api.post("/interpreter/settings", { user_id: user.id, intro_instructions: introInstr, ask_helpers: askHelpers });
      toast.success(t("interp_intro_saved"));
    } catch (e) { toast.error(t("something_wrong")); } finally { setIntroBusy(false); }
  };

  const nameOf = (id) => users.find((u) => u.id === id)?.name || id;

  const loadRules = useCallback(() => {
    api.get("/admin/buddy/rules").then((r) => setRules(r.data || [])).catch(() => setRules([]));
  }, []);
  useEffect(() => { loadRules(); }, [loadRules]);

  const addRule = async () => {
    const text = newRule.trim();
    if (!text) return;
    setBusy(true);
    try {
      await api.post("/admin/buddy/rules", { text, target_user_id: newTarget === "all" ? "" : newTarget });
      setNewRule(""); setNewTarget("all");
      toast.success(t("buddy_rule_added"));
      loadRules();
    } catch (e) { toast.error(e?.response?.data?.detail || t("something_wrong")); }
    finally { setBusy(false); }
  };

  const saveRule = async () => {
    if (!editingRule?.text?.trim()) return;
    setBusy(true);
    try {
      await api.put(`/admin/buddy/rules/${editingRule.id}`, {
        text: editingRule.text.trim(), target_user_id: editingRule.target_user_id || "",
      });
      setEditingRule(null);
      toast.success(t("buddy_rule_saved"));
      loadRules();
    } catch (e) { toast.error(t("something_wrong")); } finally { setBusy(false); }
  };

  const removeRule = async (id) => {
    try {
      await api.delete(`/admin/buddy/rules/${id}`);
      toast.success(t("buddy_rule_deleted"));
      loadRules();
    } catch (e) { toast.error(t("something_wrong")); }
  };

  const loadMems = useCallback((uid) => {
    if (!uid) return;
    setMems(null);
    api.get("/buddy/memories", { params: { user_id: uid } })
      .then((r) => setMems(r.data?.memories || [])).catch(() => setMems([]));
  }, []);

  const addMem = async () => {
    const text = newMem.trim();
    if (!text || !memUser) return;
    setBusy(true);
    try {
      await api.post("/buddy/memories", { user_id: memUser, text });
      setNewMem("");
      toast.success(t("buddy_memory_saved"));
      loadMems(memUser);
    } catch (e) { toast.error(t("something_wrong")); } finally { setBusy(false); }
  };

  const saveMem = async () => {
    if (!editingMem?.text?.trim()) return;
    setBusy(true);
    try {
      await api.put(`/buddy/memories/${editingMem.id}`, { user_id: memUser, text: editingMem.text.trim() });
      setEditingMem(null);
      toast.success(t("buddy_memory_updated"));
      loadMems(memUser);
    } catch (e) { toast.error(t("something_wrong")); } finally { setBusy(false); }
  };

  const removeMem = async (id) => {
    try {
      await api.delete(`/buddy/memories/${id}`, { params: { user_id: memUser } });
      toast.success(t("buddy_memory_deleted"));
      loadMems(memUser);
    } catch (e) { toast.error(t("something_wrong")); }
  };

  return (
    <div className="space-y-6" data-testid="admin-buddy-tab">
      {/* ---- company rules (global / per-user) ---- */}
      <div className="space-y-2">
        <h3 className="font-semibold text-sm flex items-center gap-1.5"><Bot className="h-4 w-4" /> {t("buddy_rules_title")}</h3>
        <p className="text-xs text-muted-foreground">{t("buddy_rules_hint")}</p>
        <div className="flex items-center gap-2 flex-wrap">
          <Input
            data-testid="admin-buddy-rule-input"
            placeholder={t("buddy_rule_ph")}
            value={newRule}
            onChange={(e) => setNewRule(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") addRule(); }}
            className="flex-1 min-w-[220px]"
            maxLength={500}
          />
          <Select value={newTarget} onValueChange={setNewTarget}>
            <SelectTrigger className="h-10 w-[190px] text-xs" data-testid="admin-buddy-rule-target"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("buddy_rule_target_all")}</SelectItem>
              {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button data-testid="admin-buddy-rule-add" onClick={addRule} disabled={busy || !newRule.trim()}>
            {t("buddy_rule_add")}
          </Button>
        </div>
        {rules === null && <div className="text-sm text-muted-foreground py-6 text-center">…</div>}
        {rules !== null && rules.length === 0 && <div className="text-sm text-muted-foreground py-6 text-center">—</div>}
        {(rules || []).map((r) => (
          <Card key={r.id} className="p-3 flex items-center gap-2" data-testid="admin-buddy-rule-row">
            {editingRule?.id === r.id ? (
              <>
                <Input
                  data-testid="admin-buddy-rule-edit-input"
                  value={editingRule.text}
                  onChange={(e) => setEditingRule((p) => ({ ...p, text: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === "Enter") saveRule(); }}
                  className="flex-1 h-9" maxLength={500} autoFocus
                />
                <Select
                  value={editingRule.target_user_id || "all"}
                  onValueChange={(v) => setEditingRule((p) => ({ ...p, target_user_id: v === "all" ? "" : v }))}
                >
                  <SelectTrigger className="h-9 w-[160px] text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("buddy_rule_target_all")}</SelectItem>
                    {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button size="sm" className="h-9" data-testid="admin-buddy-rule-save" onClick={saveRule} disabled={busy}>OK</Button>
                <Button size="sm" variant="ghost" className="h-9" onClick={() => setEditingRule(null)}>✕</Button>
              </>
            ) : (
              <>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{r.text}</div>
                  <Badge variant="secondary" className="mt-1 text-[10px]" data-testid="admin-buddy-rule-target-badge">
                    {r.target_user_id ? nameOf(r.target_user_id) : t("buddy_rule_target_all")}
                  </Badge>
                </div>
                <Button size="sm" variant="outline" className="h-8" data-testid="admin-buddy-rule-edit"
                  onClick={() => setEditingRule({ id: r.id, text: r.text, target_user_id: r.target_user_id || "" })}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button size="sm" variant="outline" className="h-8 text-destructive hover:text-destructive"
                  data-testid="admin-buddy-rule-delete" onClick={() => removeRule(r.id)}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </>
            )}
          </Card>
        ))}
      </div>

      {/* ---- interpreter (client mode): intro instructions + company vocabulary ---- */}
      <div className="space-y-2" data-testid="admin-interpreter-section">
        <h3 className="font-semibold text-sm flex items-center gap-1.5">
          <Languages className="h-4 w-4" /> {t("interp_admin_title")}
        </h3>
        <p className="text-xs text-muted-foreground">{t("interp_admin_hint")}</p>
        <Card className="p-3 space-y-2">
          <Label className="text-xs font-semibold">{t("interp_intro_instr_label")}</Label>
          <Textarea
            data-testid="admin-interp-intro-input"
            placeholder={t("interp_intro_instr_ph")}
            value={introInstr}
            onChange={(e) => setIntroInstr(e.target.value)}
            rows={3} maxLength={1200}
          />
          <div className="flex items-start justify-between gap-3 pt-1" data-testid="admin-interp-ask-helpers">
            <div className="min-w-0">
              <Label className="text-xs font-semibold">{t("interp_ask_helpers_label")}</Label>
              <p className="text-[11px] text-muted-foreground">{t("interp_ask_helpers_hint")}</p>
            </div>
            <Switch checked={askHelpers} onCheckedChange={setAskHelpers} data-testid="admin-interp-ask-helpers-switch" />
          </div>
          <Button size="sm" data-testid="admin-interp-intro-save" onClick={saveIntroInstr} disabled={introBusy}>
            {introBusy ? "..." : t("save")}
          </Button>
        </Card>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={newKnowKind} onValueChange={setNewKnowKind}>
            <SelectTrigger className="h-10 w-[130px] text-xs" data-testid="admin-interp-know-kind"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="term">{t("interp_kind_term")}</SelectItem>
              <SelectItem value="team">{t("interp_kind_team")}</SelectItem>
              <SelectItem value="fact">{t("interp_kind_fact")}</SelectItem>
            </SelectContent>
          </Select>
          <Input
            data-testid="admin-interp-know-input"
            placeholder={t("interp_know_ph")}
            value={newKnow}
            onChange={(e) => setNewKnow(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") addKnowledge(); }}
            className="flex-1 min-w-[200px]" maxLength={400}
          />
          <Button data-testid="admin-interp-know-add" onClick={addKnowledge} disabled={busy || !newKnow.trim()}>
            {t("buddy_rule_add")}
          </Button>
        </div>
        {knowledge === null && <div className="text-sm text-muted-foreground py-4 text-center">…</div>}
        {knowledge !== null && knowledge.length === 0 && (
          <div className="text-sm text-muted-foreground py-4 text-center">{t("interp_know_empty")}</div>
        )}
        {(knowledge || []).map((k) => (
          <Card key={k.id} className="p-3 flex items-center gap-2" data-testid="admin-interp-know-row">
            <Badge variant="secondary" className="text-[10px] shrink-0">
              {k.kind === "term" ? t("interp_kind_term") : k.kind === "team" ? t("interp_kind_team") : t("interp_kind_fact")}
            </Badge>
            <div className="flex-1 min-w-0 text-sm">{k.text}</div>
            <Button size="sm" variant="outline" className="h-8 text-destructive hover:text-destructive"
              data-testid="admin-interp-know-delete" onClick={() => removeKnowledge(k.id)}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </Card>
        ))}
      </div>

      {/* ---- MASTER: global knowledge (all companies + solo users) ---- */}
      {isMaster && (
        <div className="space-y-2 rounded-lg border-2 border-primary/40 p-3" data-testid="admin-global-knowledge-section">
          <h3 className="font-semibold text-sm flex items-center gap-1.5">
            <Globe className="h-4 w-4 text-primary" /> {t("buddy_global_title")}
            <Badge className="text-[10px] ml-1">{t("buddy_global_badge")}</Badge>
          </h3>
          <p className="text-xs text-muted-foreground">{t("buddy_global_hint")}</p>
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={newGlobalKind} onValueChange={setNewGlobalKind}>
              <SelectTrigger className="h-10 w-[130px] text-xs" data-testid="admin-global-know-kind"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="term">{t("interp_kind_term")}</SelectItem>
                <SelectItem value="team">{t("interp_kind_team")}</SelectItem>
                <SelectItem value="fact">{t("interp_kind_fact")}</SelectItem>
              </SelectContent>
            </Select>
            <Input
              data-testid="admin-global-know-input"
              placeholder={t("buddy_global_ph")}
              value={newGlobal}
              onChange={(e) => setNewGlobal(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") addGlobalKnowledge(); }}
              className="flex-1 min-w-[200px]" maxLength={400}
            />
            <Button data-testid="admin-global-know-add" onClick={addGlobalKnowledge} disabled={busy || !newGlobal.trim()}>
              {t("buddy_rule_add")}
            </Button>
          </div>
          {globalKnow === null && <div className="text-sm text-muted-foreground py-4 text-center">…</div>}
          {globalKnow !== null && globalKnow.length === 0 && (
            <div className="text-sm text-muted-foreground py-4 text-center" data-testid="admin-global-know-empty">{t("buddy_global_empty")}</div>
          )}
          {(globalKnow || []).map((k) => (
            <Card key={k.id} className="p-3 flex items-center gap-2" data-testid="admin-global-know-row">
              <Badge variant="secondary" className="text-[10px] shrink-0">
                {k.kind === "term" ? t("interp_kind_term") : k.kind === "team" ? t("interp_kind_team") : t("interp_kind_fact")}
              </Badge>
              <div className="flex-1 min-w-0 text-sm">{k.text}</div>
              <Button size="sm" variant="outline" className="h-8 text-destructive hover:text-destructive"
                data-testid="admin-global-know-delete" onClick={() => removeKnowledge(k.id)}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </Card>
          ))}
        </div>
      )}

      {/* ---- memories per user ---- */}
      <div className="space-y-2">
        <h3 className="font-semibold text-sm">{t("buddy_user_memories")}</h3>
        <Select value={memUser} onValueChange={(v) => { setMemUser(v); setEditingMem(null); loadMems(v); }}>
          <SelectTrigger className="h-10 w-[240px] text-xs" data-testid="admin-buddy-mem-user">
            <SelectValue placeholder={t("buddy_pick_user")} />
          </SelectTrigger>
          <SelectContent>
            {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {memUser && (
          <>
            <div className="flex items-center gap-2">
              <Input
                data-testid="admin-buddy-mem-input"
                placeholder={t("buddy_memory_add_ph")}
                value={newMem}
                onChange={(e) => setNewMem(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") addMem(); }}
                className="flex-1" maxLength={400}
              />
              <Button data-testid="admin-buddy-mem-add" onClick={addMem} disabled={busy || !newMem.trim()}>+</Button>
            </div>
            {mems === null && <div className="text-sm text-muted-foreground py-4 text-center">…</div>}
            {mems !== null && mems.length === 0 && (
              <div className="text-sm text-muted-foreground py-4 text-center" data-testid="admin-buddy-mem-empty">
                {t("buddy_no_memories_user")}
              </div>
            )}
            {(mems || []).map((m) => (
              <Card key={m.id} className="p-3 flex items-center gap-2" data-testid="admin-buddy-mem-row">
                {editingMem?.id === m.id ? (
                  <>
                    <Input
                      data-testid="admin-buddy-mem-edit-input"
                      value={editingMem.text}
                      onChange={(e) => setEditingMem((p) => ({ ...p, text: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === "Enter") saveMem(); }}
                      className="flex-1 h-9" maxLength={400} autoFocus
                    />
                    <Button size="sm" className="h-9" data-testid="admin-buddy-mem-save" onClick={saveMem} disabled={busy}>OK</Button>
                    <Button size="sm" variant="ghost" className="h-9" onClick={() => setEditingMem(null)}>✕</Button>
                  </>
                ) : (
                  <>
                    <span className="flex-1 text-sm min-w-0">{m.text}</span>
                    <Button size="sm" variant="outline" className="h-8" data-testid="admin-buddy-mem-edit"
                      onClick={() => setEditingMem({ id: m.id, text: m.text })}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="outline" className="h-8 text-destructive hover:text-destructive"
                      data-testid="admin-buddy-mem-delete" onClick={() => removeMem(m.id)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </>
                )}
              </Card>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

export default function AdminPage() {  const { user, t, lang } = useApp();
  const isMaster = !!user?.super_admin; // MASTER: single system owner account
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [stats, setStats] = useState(null);
  const [pmhr, setPmhr] = useState(null);
  const [pmhrLogs, setPmhrLogs] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [deleteUser, setDeleteUser] = useState(null);
  const [resetUser, setResetUser] = useState(null);
  const [resetBusy, setResetBusy] = useState(false);
  const factoryReset = async () => {
    if (!resetUser) return;
    setResetBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${resetUser.id}/factory-reset`);
      const n = Object.values(data?.deleted || {}).reduce((a, b) => a + b, 0);
      toast.success(`${t("factory_reset_done")} (${n})`);
      setResetUser(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("something_wrong"));
    } finally { setResetBusy(false); }
  };
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [seedBusy, setSeedBusy] = useState(false);
  const [zipBusy, setZipBusy] = useState(false);
  const [orphanBusy, setOrphanBusy] = useState(false);
  const [orphanConfirm, setOrphanConfirm] = useState(false);
  const [zipPhase, setZipPhase] = useState(null); // 'zipping' | 'downloading'
  const [zipPct, setZipPct] = useState(0);
  const [zipInfo, setZipInfo] = useState("");
  const [dupResult, setDupResult] = useState(null);
  const [dupBusy, setDupBusy] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [crews, setCrews] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false); // add EXISTING account to my company
  const [inviteTarget, setInviteTarget] = useState(null); // not registered yet -> offer an invite
  const [addIdentifier, setAddIdentifier] = useState("");
  const [addRole, setAddRole] = useState("installer");
  const [addBusy, setAddBusy] = useState(false);
  const [sentInvites, setSentInvites] = useState([]); // pending company invites
  const [allAccounts, setAllAccounts] = useState([]); // MASTER: every account + banner flags
  const [storeVersions, setStoreVersions] = useState(null); // MASTER: latest App Store / Play versions
  // MASTER: filters + sorting for the global account list
  const [mQ, setMQ] = useState("");
  const [mCompany, setMCompany] = useState("all");
  const [mRole, setMRole] = useState("all");
  const [mSort, setMSort] = useState("name_asc");

  const masterCompanies = useMemo(() => {
    const s = new Set();
    allAccounts.forEach((a) => { if (a.company_name) s.add(a.company_name); });
    return [...s].sort((x, y) => x.localeCompare(y));
  }, [allAccounts]);

  const masterRoles = useMemo(() => {
    const s = new Set();
    allAccounts.forEach((a) => { if (a.role) s.add(a.role); });
    return [...s].sort();
  }, [allAccounts]);

  // MASTER: ONE unified list — full user objects (/admin/users returns EVERY
  // account for the master) enriched with company name + banner flags coming
  // from /admin/all-users. Editing, deleting, role changes and banner toggles
  // all live on the same row.
  const masterAccounts = useMemo(() => {
    const extra = {};
    allAccounts.forEach((a) => { extra[a.id] = a; });
    return users
      .filter((u) => !u.super_admin) // the master itself is not managed here
      .map((u) => ({
        ...u,
        company_name: extra[u.id]?.company_name || "",
        show_tour: !!(extra[u.id]?.show_tour ?? u.show_tour),
        ai_consent_pending: extra[u.id] ? !!extra[u.id].ai_consent_pending : u.ai_consent == null,
        buddy_enabled: extra[u.id] ? !!extra[u.id].buddy_enabled : buddyEnabled(u),
      }));
  }, [users, allAccounts]);

  const filteredAccounts = useMemo(() => {
    const q = mQ.trim().toLowerCase();
    const list = masterAccounts.filter((a) => {
      if (q && !`${a.name || ""} ${a.email || ""} ${a.company_name || ""}`.toLowerCase().includes(q)) return false;
      if (mCompany === "none") { if (a.company_name) return false; }
      else if (mCompany !== "all" && a.company_name !== mCompany) return false;
      if (mRole !== "all" && a.role !== mRole) return false;
      return true;
    });
    const byName = (x, y) => (x.name || x.email || "").localeCompare(y.name || y.email || "");
    const sorters = {
      name_asc: byName,
      name_desc: (x, y) => byName(y, x),
      company: (x, y) => (x.company_name || "\uffff").localeCompare(y.company_name || "\uffff") || byName(x, y),
      role: (x, y) => (x.role || "\uffff").localeCompare(y.role || "\uffff") || byName(x, y),
      pending: (x, y) =>
        (Number(!!y.show_tour || !!y.ai_consent_pending) - Number(!!x.show_tour || !!x.ai_consent_pending)) || byName(x, y),
    };
    return [...list].sort(sorters[mSort] || byName);
  }, [masterAccounts, mQ, mCompany, mRole, mSort]);

  // MASTER: re-show (or cancel) a first-login banner for one account
  const toggleBanner = async (uid, patch) => {
    try {
      const { data } = await api.patch(`/admin/users/${uid}/banners`, patch);
      setAllAccounts((list) => list.map((a) => (a.id === uid
        ? { ...a, show_tour: data.show_tour, ai_consent_pending: data.ai_consent_pending, buddy_enabled: data.buddy_enabled }
        : a)));
    } catch (e) {
      toast.error(t("something_wrong"));
    }
  };
  const [memberEvents, setMemberEvents] = useState([]); // joined/left/removed history
  const [historyOpen, setHistoryOpen] = useState(false);
  const [pmhrLinks, setPmhrLinks] = useState([]); // sync links: crew/supervisor -> user
  const [linkableGroups, setLinkableGroups] = useState([]); // own + partner companies' users
  const [linkPick, setLinkPick] = useState({}); // link_id -> chosen user_id
  const [linkBusy, setLinkBusy] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null); // member pending remove-from-company confirm
  const [loading, setLoading] = useState(true);
  const [pendingRegs, setPendingRegs] = useState([]);
  const [regBusy, setRegBusy] = useState(null); // reg id being decided
  const [syncCfg, setSyncCfg] = useState({ base_url: "", username: "", password: "", interval_minutes: 5, window_past_days: 30, window_future_days: 30, service_supervisor_id: "" });
  const [savingSyncCfg, setSavingSyncCfg] = useState(false);
  const [syncTest, setSyncTest] = useState(null); // result of "test connection"
  const [testingSync, setTestingSync] = useState(false);

  // Friendly, explicit explanations for every sync error code
  const syncErrorText = (code) => ({
    not_configured: t("sync_err_not_configured"),
    invalid_url: t("sync_err_invalid_url"),
    connection_failed: t("sync_err_connection"),
    timeout: t("sync_err_timeout"),
    auth_failed: t("sync_err_auth"),
    server_error: t("sync_err_server"),
    bad_response: t("sync_err_bad_response"),
  }[code] || t("sync_err_generic"));

  const testSyncConnection = async () => {
    setTestingSync(true);
    setSyncTest(null);
    try {
      const { data } = await api.post("/admin/pmhr/test");
      setSyncTest(data);
    } catch (e) {
      setSyncTest({ ok: false, error_code: "connection_failed" });
    } finally { setTestingSync(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, tk, st, pm, pl, cr, pr, si, mh, lk, lu, aa] = await Promise.all([
        api.get("/admin/users"),
        api.get("/admin/device-tokens"),
        api.get("/admin/stats").catch(() => ({ data: null })),
        api.get("/admin/pmhr"),
        api.get("/admin/pmhr/logs"),
        api.get("/crews"),
        api.get("/auth/registrations/pending").catch(() => ({ data: [] })),
        api.get("/company/invites/sent").catch(() => ({ data: [] })),
        api.get("/company/member-history").catch(() => ({ data: [] })),
        api.get("/admin/pmhr/links").catch(() => ({ data: [] })),
        api.get("/admin/pmhr/linkable-users").catch(() => ({ data: [] })),
        user?.super_admin ? api.get("/admin/all-users").catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      ]);
      setUsers(u.data);
      setTokens(tk.data);
      setStats(st.data);
      setPmhr(pm.data);
      setPmhrLogs(pl.data);
      setCrews(cr.data);
      setPendingRegs(pr.data || []);
      setSentInvites(si.data || []);
      setMemberEvents(mh.data || []);
      setPmhrLinks(lk.data || []);
      setLinkableGroups(Array.isArray(lu.data) ? lu.data : []);
      setAllAccounts(Array.isArray(aa.data) ? aa.data : []);
      if (user?.super_admin) api.get("/app/store-versions").then((r) => setStoreVersions(r.data)).catch(() => {});
      if (pm.data) {
        setSyncCfg((s) => ({
          ...s,
          base_url: pm.data.base_url || "",
          username: pm.data.username || "",
          interval_minutes: pm.data.interval_minutes ?? 5,
          window_past_days: pm.data.window_past_days ?? 30,
          window_future_days: pm.data.window_future_days ?? 30,
          service_supervisor_id: pm.data.service_supervisor_id || "",
        }));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("something_wrong"));
    } finally { setLoading(false); }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const [leaveReqs, setLeaveReqs] = useState([]);
  useEffect(() => {
    if (user?.role !== "admin") return;
    api.get("/company/leave-requests").then((r) => setLeaveReqs(r.data || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingRegs]);

  if (user?.role !== "admin") {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <p className="text-sm text-muted-foreground" data-testid="admin-denied">{t("admin_only")}</p>
      </div>
    );
  }

  const decideRegistration = async (reg, action) => {
    setRegBusy(reg.id);
    try {
      await api.post(`/auth/registrations/${reg.id}/decide`, { action });
      toast.success(action === "approve" ? `${t("reg_approved_toast")} — ${reg.name}` : `${t("reg_rejected_toast")} — ${reg.name}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("something_wrong"));
    } finally { setRegBusy(null); }
  };

  const decideLeave = async (req, action) => {
    try {
      await api.post(`/company/leave-requests/${req.id}/decide`, { action });
      toast.success(action === "approve" ? `${t("leave_approved_toast")} — ${req.user_name}` : `${t("leave_rejected_toast")} — ${req.user_name}`);
      setLeaveReqs((l) => l.filter((x) => x.id !== req.id));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("something_wrong"));
    }
  };

  const removeMember = async () => {
    const u = removeTarget;
    setRemoveTarget(null);
    if (!u) return;
    try {
      await api.post(`/company/members/${u.id}/remove`);
      toast.success(`${t("member_removed_toast")} — ${u.name}`);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      toast.error(detail === "last_admin" ? t("last_admin_error") : (detail || t("something_wrong")));
    }
  };

  const addExistingUser = async (e) => {
    e.preventDefault();
    if (!addIdentifier.trim()) return;
    setAddBusy(true);
    try {
      await api.post("/company/members/add", { identifier: addIdentifier.trim(), role: addRole });
      toast.success(t("add_existing_user_toast"));
      setAddOpen(false);
      setAddIdentifier("");
      setAddRole("installer");
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail || "";
      if (detail === "user_not_found") {
        if (looksLikeIdentifier(addIdentifier)) { setAddOpen(false); setInviteTarget(addIdentifier.trim()); }
        else toast.error(t("invalid_connect_code"));
      }
      else if (detail === "already_member") toast.error(t("already_member"));
      else if (detail === "belongs_to_other_company") toast.error(t("belongs_to_other_company"));
      else toast.error(t("something_wrong"));
    } finally { setAddBusy(false); }
  };

  const saveSyncCfg = async () => {
    setSavingSyncCfg(true);
    try {
      const payload = {
        base_url: syncCfg.base_url.trim(),
        username: syncCfg.username.trim(),
        interval_minutes: parseInt(syncCfg.interval_minutes, 10) || 5,
        window_past_days: parseInt(syncCfg.window_past_days, 10) || 30,
        window_future_days: parseInt(syncCfg.window_future_days, 10) || 30,
        service_supervisor_id: syncCfg.service_supervisor_id || "",
      };
      if (syncCfg.password.trim()) payload.password = syncCfg.password.trim();
      await api.put("/admin/pmhr/settings", payload);
      toast.success(t("sync_saved_toast"));
      setSyncCfg((s) => ({ ...s, password: "" }));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("something_wrong"));
    } finally { setSavingSyncCfg(false); }
  };

  const changeRole = async (u, role) => {
    try {
      await api.patch(`/admin/users/${u.id}`, { role });
      toast.success(t("saved"));
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      toast.error(detail === "last_admin" ? t("last_admin_error") : (detail || t("something_wrong")));
    }
  };

  const clearPasskeys = async (u) => {
    try {
      await api.delete(`/admin/users/${u.id}/passkeys`);
      toast.success(t("passkeys_cleared"));
      load();
    } catch (e) { toast.error(t("something_wrong")); }
  };

  const deleteToken = async (tk) => {
    try {
      await api.delete(`/admin/device-tokens/${tk.id}`);
      toast.success(t("token_removed"));
      load();
    } catch (e) { toast.error(t("something_wrong")); }
  };

  const testPush = async (uid) => {
    try {
      const res = await api.post("/admin/push-test", { user_id: uid, title: "OnSiteComms", body: t("test_push_body") });
      toast.success(`${t("test_push_sent")} (${res.data.devices})`);
    } catch (e) { toast.error(e?.response?.data?.detail || t("something_wrong")); }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const res = await api.post("/admin/pmhr/sync-now", {}, { timeout: 300000 });
      const r = res.data;
      toast.success(`${t("pmhr_sync_done")}: +${r.created} ~${r.updated} -${r.deleted} · ${r.pdfs} PDFs`);
      if (r.pending_links > 0) toast.warning(t("pmhr_links_pending_toast"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("something_wrong"));
    } finally { setSyncing(false); }
  };

  const assignPmhrLink = async (link) => {
    const uid = linkPick[link.id];
    if (!uid) return;
    setLinkBusy(link.id);
    try {
      const { data } = await api.post(`/admin/pmhr/links/${link.id}/assign`, { user_id: uid });
      toast.success(`${t("pmhr_link_saved")} — ${data.user_name || ""}`);
      const { data: fresh } = await api.get("/admin/pmhr/links");
      setPmhrLinks(fresh || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("something_wrong"));
    } finally { setLinkBusy(null); }
  };

  const toggleAutoSync = async (enabled) => {
    setPmhr((p) => ({ ...p, enabled }));
    try {
      await api.put("/admin/pmhr/settings", { enabled });
      toast.success(t("saved"));
    } catch (e) {
      toast.error(t("something_wrong"));
      load();
    }
  };

  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString(lang === "pt" ? "pt-BR" : "en-US") : t("pmhr_never"));

  const confirmDeleteUser = async () => {
    if (!deleteUser) return;
    try {
      await api.delete(`/admin/users/${deleteUser.id}`);
      toast.success(t("user_deleted"));
      setDeleteUser(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("something_wrong"));
    }
  };

  const cleanupOrphans = async () => {
    setOrphanConfirm(false);
    setOrphanBusy(true);
    try {
      const { data } = await api.post("/admin/cleanup-orphans");
      toast.success(
        t("cleanup_orphans_done")
          .replace("{m}", data.messages).replace("{i}", data.issues)
          .replace("{t}", data.tasks).replace("{f}", data.media));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("something_wrong"));
    } finally { setOrphanBusy(false); }
  };

  const downloadSource = async () => {
    setZipBusy(true);
    setZipPhase("zipping");
    setZipPct(0);
    setZipInfo("");
    try {
      // Phase 1 — server builds the ZIP; poll REAL progress (files done/total)
      await api.post("/admin/source-zip/start");
      let zipSize = 0;
      for (;;) {
        await new Promise((r) => setTimeout(r, 500));
        const { data } = await api.get("/admin/source-zip/status");
        if (data.state === "zipping") {
          const pct = data.total ? Math.round((data.done / data.total) * 100) : 0;
          setZipPct(pct);
          setZipInfo(`${data.done.toLocaleString()} / ${data.total.toLocaleString()} ${t("zip_files")}`);
        } else if (data.state === "ready") {
          zipSize = data.size || 0;
          setZipPct(100);
          break;
        } else if (data.state === "error") {
          throw new Error(data.error || "zip failed");
        }
      }
      // Phase 2 — download with REAL byte progress
      setZipPhase("downloading");
      setZipPct(0);
      setZipInfo("");
      const res = await api.get("/admin/source-zip", {
        responseType: "blob",
        timeout: 600000,
        onDownloadProgress: (e) => {
          const total = e.total || zipSize;
          if (total > 0) {
            setZipPct(Math.min(100, Math.round((e.loaded / total) * 100)));
            setZipInfo(`${(e.loaded / 1048576).toFixed(1)} / ${(total / 1048576).toFixed(1)} MB`);
          } else {
            setZipInfo(`${(e.loaded / 1048576).toFixed(1)} MB`);
          }
        },
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "onsitecomms-source.zip";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setZipPct(100);
      toast.success(t("zip_done"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || t("something_wrong"));
    } finally {
      setZipBusy(false);
      setZipPhase(null);
    }
  };

  const cleanupDemo = async () => {
    setCleanupOpen(false);
    try {
      const res = await api.post("/admin/cleanup-demo-data", {}, { timeout: 120000 });
      const r = res.data;
      toast.success(`${t("demo_cleaned")} — users: ${r.users}, jobs: ${r.jobs}, msgs: ${r.messages}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("something_wrong"));
    }
  };

  const seedDemo = async () => {
    setSeedBusy(true);
    try {
      const res = await api.post("/admin/seed-demo-account", {}, { timeout: 120000 });
      toast.success(`${t("seed_demo_done")} — ${res.data.email}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("something_wrong"));
    } finally {
      setSeedBusy(false);
    }
  };

  const checkDuplicates = async () => {
    setDupBusy(true);
    try {
      const res = await api.get("/admin/pmhr/duplicates");
      setDupResult(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("something_wrong"));
    } finally { setDupBusy(false); }
  };

  const deleteDuplicates = async () => {
    setDupBusy(true);
    try {
      const res = await api.post("/admin/pmhr/dedupe", {}, { timeout: 120000 });
      toast.success(`${t("dup_deleted")}: ${res.data.jobs_removed} jobs, ${res.data.pdfs_removed} PDFs`);
      await checkDuplicates();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("something_wrong"));
      setDupBusy(false);
    }
  };

  return (
    <div className="flex-1 max-w-4xl mx-auto w-full px-4 py-4 pb-8" data-testid="admin-page">
      <div className="flex items-center gap-2 mb-4">
        <Button variant="ghost" size="icon" data-testid="admin-back" onClick={() => navigate("/more")}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <ShieldCheck className="h-6 w-6 text-primary" />
        <h1 className="font-display font-bold text-2xl tracking-tight flex-1">{t("admin_panel")}</h1>
        <Button variant="ghost" size="icon" data-testid="admin-refresh" onClick={load}>
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      <Tabs defaultValue="users">
        <TabsList className={`w-full flex justify-start gap-0.5 overflow-x-auto md:grid ${isMaster ? "md:grid-cols-8" : "md:grid-cols-6"} [scrollbar-width:none] [&::-webkit-scrollbar]:hidden`}>
          <TabsTrigger className="shrink-0" value="users" data-testid="admin-tab-users">
            <UsersIcon className="h-4 w-4 mr-1.5" />{t("users")}
            {pendingRegs.length > 0 && (
              <span className="ml-1.5 inline-flex items-center justify-center h-4 min-w-4 px-1 rounded-full bg-[#C9A03B] text-white text-[10px] font-bold" data-testid="admin-pending-regs-badge">
                {pendingRegs.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger className="shrink-0" value="company" data-testid="admin-tab-company"><Building2 className="h-4 w-4 mr-1.5" />{t("business_cards_tab")}</TabsTrigger>
          {isMaster && (
            <TabsTrigger className="shrink-0" value="chats" data-testid="admin-tab-chats"><MessageSquare className="h-4 w-4 mr-1.5" />{t("chats_tab")}</TabsTrigger>
          )}
          <TabsTrigger className="shrink-0" value="quick" data-testid="admin-tab-quick"><Zap className="h-4 w-4 mr-1.5" />{t("quick_replies_tab")}</TabsTrigger>
          <TabsTrigger className="shrink-0" value="buddy" data-testid="admin-tab-buddy"><Bot className="h-4 w-4 mr-1.5" />Buddy</TabsTrigger>
          <TabsTrigger className="shrink-0" value="pmhr" data-testid="admin-tab-pmhr"><FolderSync className="h-4 w-4 mr-1.5" />{t("pmhr_tab")}</TabsTrigger>
          {isMaster && (
            <TabsTrigger className="shrink-0" value="push" data-testid="admin-tab-push"><Bell className="h-4 w-4 mr-1.5" />Push</TabsTrigger>
          )}
          {isMaster && (
            <TabsTrigger className="shrink-0" value="system" data-testid="admin-tab-system"><Database className="h-4 w-4 mr-1.5" />{t("system")}</TabsTrigger>
          )}
        </TabsList>

        {/* ---------------- BUSINESS CARDS (my company card + partners) ---------------- */}
        <TabsContent value="company" className="mt-4">
          <CompanyCardsTab t={t} />
        </TabsContent>

        {/* ---------------- BUDDY (company rules + user memories) ---------------- */}
        <TabsContent value="buddy" className="mt-4">
          <BuddyAdminTab t={t} users={users} />
        </TabsContent>

        {/* ---------------- QUICK REPLIES (⚡ composer phrases) ---------------- */}
        <TabsContent value="quick" className="mt-4">
          <QuickRepliesTab t={t} />
        </TabsContent>

        {/* ---------------- CONVERSATIONS (global audit — MASTER ONLY) ---------------- */}
        {isMaster && (
        <TabsContent value="chats" className="mt-4">
          <AdminConversationsTab t={t} />
        </TabsContent>
        )}

        {/* ---------------- USERS ---------------- */}
        <TabsContent value="users" className="mt-4 space-y-2">
          {/* MASTER: every account in the system + first-login banner control */}
          {isMaster && <StoreVersionsCard t={t} lang={lang} />}
          {isMaster && <ForceUpdateCard t={t} />}
          {isMaster && (
            <Card className="p-3" data-testid="master-all-users-card">
              <h3 className="font-semibold text-sm mb-1 flex items-center gap-2">
                <UsersIcon className="h-4 w-4 text-primary" /> {t("master_users_title")}
                <Badge variant="secondary" className="text-[10px]" data-testid="master-users-count">
                  {filteredAccounts.length === masterAccounts.length
                    ? masterAccounts.length
                    : `${filteredAccounts.length} / ${masterAccounts.length}`}
                </Badge>
              </h3>
              <p className="text-[11px] text-muted-foreground mb-2 leading-relaxed">{t("master_banners_hint")}</p>
              {/* Filters + sorting */}
              <div className="space-y-1.5 mb-2">
                <Input
                  value={mQ}
                  onChange={(e) => setMQ(e.target.value)}
                  placeholder={t("master_users_search_ph")}
                  className="h-8 text-sm"
                  data-testid="master-users-search"
                />
                <div className="flex gap-1.5 flex-wrap">
                  <Select value={mCompany} onValueChange={setMCompany}>
                    <SelectTrigger className="h-8 text-xs flex-1 min-w-[105px]" data-testid="master-users-company-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t("all_companies")}</SelectItem>
                      <SelectItem value="none">{t("master_no_company")}</SelectItem>
                      {masterCompanies.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={mRole} onValueChange={setMRole}>
                    <SelectTrigger className="h-8 text-xs flex-1 min-w-[95px]" data-testid="master-users-role-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t("all_roles")}</SelectItem>
                      {masterRoles.map((r) => <SelectItem key={r} value={r}>{t(r)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={mSort} onValueChange={setMSort}>
                    <SelectTrigger className="h-8 text-xs flex-1 min-w-[110px]" data-testid="master-users-sort">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="name_asc">{t("master_sort_name_asc")}</SelectItem>
                      <SelectItem value="name_desc">{t("master_sort_name_desc")}</SelectItem>
                      <SelectItem value="company">{t("master_sort_company")}</SelectItem>
                      <SelectItem value="role">{t("master_sort_role")}</SelectItem>
                      <SelectItem value="pending">{t("master_sort_pending")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5 max-h-[65vh] overflow-y-auto">
                {filteredAccounts.map((a) => (
                  <div key={a.id} className="rounded-lg border px-3 py-2 space-y-1.5" data-testid={`master-user-row-${a.id}`}>
                    <div className="min-w-0">
                      <div className="text-sm font-medium flex items-center gap-1.5 min-w-0">
                        <span className="truncate">{a.name || a.email}</span>
                        {a.apple_linked && <Apple className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
                        {a.passkey_count > 0 && <Fingerprint className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
                        {a.device_count > 0 && (
                          <span className="inline-flex items-center text-[10px] text-muted-foreground shrink-0">
                            <Smartphone className="h-3 w-3" />{a.device_count}
                          </span>
                        )}
                        <UserAppVersion a={a} store={storeVersions} />
                      </div>
                      <div className="text-[11px] text-muted-foreground truncate">
                        {a.email}{a.company_name ? ` · ${a.company_name}` : ` · ${t("master_no_company")}`}
                      </div>
                      <div className="text-[10px] text-muted-foreground">
                        {t("last_login")}: {a.last_login_at ? new Date(a.last_login_at).toLocaleString(lang === "pt" ? "pt-BR" : "en-US") : "—"}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <Select value={a.role} onValueChange={(v) => changeRole(a, v)}>
                        <SelectTrigger className="h-8 w-[130px] text-xs" data-testid={`master-user-role-${a.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {ROLES.map((r) => <SelectItem key={r} value={r}>{t(r)}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Button size="sm" variant="outline" className="h-8" data-testid={`master-user-edit-${a.id}`} onClick={() => setEditUser(a)}>
                        <Pencil className="h-3.5 w-3.5 mr-1" /> {t("edit")}
                      </Button>
                      {a.passkey_count > 0 && (
                        <Button size="sm" variant="outline" className="h-8" data-testid={`master-user-clear-passkeys-${a.id}`} onClick={() => clearPasskeys(a)}>
                          <Fingerprint className="h-3.5 w-3.5 mr-1" /> {t("clear")}
                        </Button>
                      )}
                      {a.company_name && (
                        <Button size="sm" variant="outline" className="h-8" title={t("remove_from_company")} data-testid={`master-user-remove-company-${a.id}`} onClick={() => setRemoveTarget(a)}>
                          <UserX className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      <Button size="sm" variant="outline" className="h-8 text-amber-700 hover:text-amber-800" title={t("factory_reset")} data-testid={`master-user-reset-${a.id}`} onClick={() => setResetUser(a)}>
                        <RotateCcw className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="outline" className="h-8 text-destructive hover:text-destructive" data-testid={`master-user-delete-${a.id}`} onClick={() => setDeleteUser(a)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                    <div className="flex items-center gap-4 flex-wrap pt-0.5 border-t border-dashed">
                      <label className="flex items-center gap-1.5 text-[11px] cursor-pointer select-none">
                        <Switch
                          className="scale-75 -mx-1"
                          checked={!!a.show_tour}
                          onCheckedChange={(v) => toggleBanner(a.id, { show_tour: !!v })}
                          data-testid={`master-banner-tour-${a.id}`}
                        />
                        {t("banner_tour")}
                      </label>
                      <label className="flex items-center gap-1.5 text-[11px] cursor-pointer select-none">
                        <Switch
                          className="scale-75 -mx-1"
                          checked={!!a.ai_consent_pending}
                          onCheckedChange={(v) => toggleBanner(a.id, { show_ai_consent: !!v })}
                          data-testid={`master-banner-ai-${a.id}`}
                        />
                        {t("banner_ai")}
                      </label>
                      <label className="flex items-center gap-1.5 text-[11px] cursor-pointer select-none">
                        <Switch
                          className="scale-75 -mx-1"
                          checked={!!a.buddy_enabled}
                          onCheckedChange={(v) => toggleBanner(a.id, { buddy_enabled: !!v })}
                          data-testid={`master-buddy-enabled-${a.id}`}
                        />
                        <Bot className="h-3 w-3 text-primary" /> {t("banner_buddy")}
                      </label>
                    </div>
                  </div>
                ))}
                {filteredAccounts.length === 0 && (
                  <p className="text-center text-sm text-muted-foreground py-4" data-testid="master-users-empty">
                    {masterAccounts.length > 0 ? t("master_filter_no_results") : "—"}
                  </p>
                )}
              </div>
            </Card>
          )}
          {/* Company business card + join code + partners now live in the "Business Cards" tab */}
          {/* Invites sent to EXISTING accounts — waiting for the person to accept */}
          {sentInvites.length > 0 && (
            <Card className="p-3" data-testid="sent-invites-card">
              <h3 className="font-semibold text-sm mb-2 flex items-center gap-2">
                <UserCheck className="h-4 w-4 text-primary" /> {t("pending_invites_title")}
                <Badge variant="secondary" className="text-[10px]">{sentInvites.length}</Badge>
              </h3>
              <div className="space-y-1.5">
                {sentInvites.map((inv) => (
                  <div key={inv.id} className="flex items-center gap-2 rounded-lg border px-3 py-2" data-testid={`sent-invite-row-${inv.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{inv.invited_user_name}</div>
                      <div className="text-[11px] text-muted-foreground truncate">{inv.invited_user_email} · {t(inv.role)}</div>
                    </div>
                    <Button
                      size="sm" variant="ghost" className="h-8 text-muted-foreground hover:text-destructive"
                      data-testid={`cancel-invite-btn-${inv.id}`}
                      onClick={async () => {
                        try {
                          await api.post(`/company/invites/${inv.id}/cancel`);
                          toast.success(t("invite_cancelled_toast"));
                          load();
                        } catch (e) { toast.error(t("something_wrong")); }
                      }}
                    >
                      <UserX className="h-3.5 w-3.5 mr-1" /> {t("cancel_invite")}
                    </Button>
                  </div>
                ))}
              </div>
            </Card>
          )}
          {/* Leave requests (member wants OUT — needs admin approval) */}
          {leaveReqs.length > 0 && (
            <Card className="p-3 border-destructive/30 bg-destructive/5" data-testid="admin-leave-reqs-card">
              <div className="flex items-center gap-2 mb-2">
                <UserX className="h-4 w-4 text-destructive" />
                <h3 className="font-semibold text-sm flex-1">{t("leave_requests_title")}</h3>
                <Badge variant="secondary" className="text-[10px]">{leaveReqs.length}</Badge>
              </div>
              <div className="space-y-2">
                {leaveReqs.map((r) => (
                  <div key={r.id} className="flex items-center gap-3 flex-wrap rounded-lg bg-background border p-2.5" data-testid="admin-leave-req-row">
                    <div className="flex-1 min-w-[160px]">
                      <div className="font-semibold text-sm">{r.user_name}</div>
                      <div className="text-xs text-muted-foreground truncate">{r.user_email}</div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Button size="sm" className="h-8 bg-[#166534] hover:bg-[#166534]/90 text-white" data-testid="admin-leave-approve-btn" onClick={() => decideLeave(r, "approve")}>
                        <UserCheck className="h-3.5 w-3.5 mr-1" /> {t("reg_approve")}
                      </Button>
                      <Button size="sm" variant="outline" className="h-8 text-destructive hover:text-destructive" data-testid="admin-leave-reject-btn" onClick={() => decideLeave(r, "reject")}>
                        <UserX className="h-3.5 w-3.5 mr-1" /> {t("reg_reject")}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
          {/* Pending account requests (approval-gated registration) */}
          {pendingRegs.length > 0 && (
            <Card className="p-3 border-[#C9A03B]/40 bg-[#C9A03B]/5" data-testid="admin-pending-regs-card">
              <div className="flex items-center gap-2 mb-2">
                <MailQuestion className="h-4 w-4 text-[#C9A03B]" />
                <h3 className="font-semibold text-sm flex-1">{t("reg_requests_title")}</h3>
                <Badge variant="secondary" className="text-[10px]">{pendingRegs.length}</Badge>
              </div>
              <div className="space-y-2">
                {pendingRegs.map((r) => (
                  <div key={r.id} className="flex items-start gap-3 flex-wrap rounded-lg bg-background border p-2.5" data-testid="admin-pending-reg-row">
                    <div className="flex-1 min-w-[160px]">
                      <div className="font-semibold text-sm">{r.name}</div>
                      <div className="text-xs text-muted-foreground truncate">{r.email}</div>
                      <div className="text-[11px] text-muted-foreground mt-0.5">
                        {t(r.role)} · {r.created_at ? new Date(r.created_at).toLocaleString(lang === "pt" ? "pt-BR" : "en-US") : ""}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Button
                        size="sm"
                        className="h-8 bg-[#166534] hover:bg-[#166534]/90 text-white"
                        data-testid="admin-reg-approve-btn"
                        disabled={regBusy === r.id}
                        onClick={() => decideRegistration(r, "approve")}
                      >
                        <UserCheck className="h-3.5 w-3.5 mr-1" /> {t("reg_approve")}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 text-destructive hover:text-destructive"
                        data-testid="admin-reg-reject-btn"
                        disabled={regBusy === r.id}
                        onClick={() => decideRegistration(r, "reject")}
                      >
                        <UserX className="h-3.5 w-3.5 mr-1" /> {t("reg_reject")}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            <Button data-testid="admin-create-user-btn" size="sm" onClick={() => setCreateOpen(true)}>
              <UserPlus className="h-4 w-4 mr-1.5" /> {t("create_user")}
            </Button>
            <Button data-testid="admin-add-user-btn" size="sm" variant="outline" onClick={() => setAddOpen(true)}>
              <UserCheck className="h-4 w-4 mr-1.5" /> {t("add_existing_user")}
            </Button>
          </div>
          {!isMaster && users.map((u) => (
            <Card key={u.id} className="p-3" data-testid="admin-user-card">
              <div className="flex items-start gap-3 flex-wrap">
                <div className="flex-1 min-w-[180px]">
                  <div className="font-semibold text-sm flex items-center gap-1.5">
                    {u.name}
                    {u.apple_linked && <Apple className="h-3.5 w-3.5 text-muted-foreground" />}
                    {u.passkey_count > 0 && <Fingerprint className="h-3.5 w-3.5 text-muted-foreground" />}
                    {u.device_count > 0 && (
                      <span className="inline-flex items-center text-[10px] text-muted-foreground">
                        <Smartphone className="h-3 w-3" />{u.device_count}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">
                    {t("last_login")}: {u.last_login_at ? new Date(u.last_login_at).toLocaleString(lang === "pt" ? "pt-BR" : "en-US") : "—"}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <Select value={u.role} onValueChange={(v) => changeRole(u, v)}>
                    <SelectTrigger className="h-8 w-[130px] text-xs" data-testid="admin-user-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ROLES.map((r) => <SelectItem key={r} value={r}>{t(r)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button size="sm" variant="outline" className="h-8" data-testid="admin-user-edit-btn" onClick={() => setEditUser(u)}>
                    <Pencil className="h-3.5 w-3.5 mr-1" /> {t("edit")}
                  </Button>
                  {u.passkey_count > 0 && (
                    <Button size="sm" variant="outline" className="h-8" data-testid="admin-user-clear-passkeys" onClick={() => clearPasskeys(u)}>
                      <Fingerprint className="h-3.5 w-3.5 mr-1" /> {t("clear")}
                    </Button>
                  )}
                  {u.id !== user.id && (
                    <Button size="sm" variant="outline" className="h-8" title={t("remove_from_company")} data-testid="admin-user-remove-company-btn" onClick={() => setRemoveTarget(u)}>
                      <UserX className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  {u.id !== user.id && (
                    <Button size="sm" variant="outline" className="h-8 text-destructive hover:text-destructive" data-testid="admin-user-delete-btn" onClick={() => setDeleteUser(u)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          ))}

          {/* Member history: who joined / left / was removed, with dates */}
          <Card className="p-3" data-testid="member-history-card">
            <button
              type="button"
              className="w-full flex items-center gap-2 text-left"
              data-testid="member-history-toggle"
              onClick={() => setHistoryOpen((v) => !v)}
            >
              <Clock3 className="h-4 w-4 text-primary" />
              <h3 className="font-semibold text-sm flex-1">{t("member_history_title")}</h3>
              {memberEvents.length > 0 && <Badge variant="secondary" className="text-[10px]">{memberEvents.length}</Badge>}
              <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${historyOpen ? "rotate-180" : ""}`} />
            </button>
            {historyOpen && (
              <div className="mt-2 space-y-1 max-h-72 overflow-y-auto">
                {memberEvents.length === 0 && (
                  <p className="text-xs text-muted-foreground py-2" data-testid="member-history-empty">{t("mh_empty")}</p>
                )}
                {memberEvents.map((ev) => {
                  const colors = {
                    joined: "text-emerald-600", created: "text-emerald-600", promoted: "text-[#2B3990]",
                    left: "text-amber-600", removed: "text-destructive", deleted: "text-destructive",
                  };
                  return (
                    <div key={ev.id} className="flex items-center gap-2 rounded-lg border px-3 py-1.5" data-testid={`member-history-row-${ev.id}`}>
                      <div className="flex-1 min-w-0">
                        <span className="text-xs font-medium">{ev.user_name}</span>{" "}
                        <span className={`text-xs ${colors[ev.event] || "text-muted-foreground"}`}>{t(`mh_${ev.event}`)}</span>
                        {ev.by_name && <span className="text-[11px] text-muted-foreground"> · {t("mh_by")} {ev.by_name}</span>}
                      </div>
                      <span className="text-[10px] text-muted-foreground shrink-0">
                        {new Date(ev.at).toLocaleDateString()} {new Date(ev.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </TabsContent>

        {/* ---------------- PMHR SYNC ---------------- */}
        <TabsContent value="pmhr" className="mt-4 space-y-3">
          {/* Sync links: crew/supervisor from the schedule -> EXISTING user */}
          {pmhrLinks.length > 0 && (
            <Card className="p-4 space-y-2.5" data-testid="pmhr-links-card">
              <div className="flex items-center gap-2">
                <Link2 className="h-4 w-4 text-primary shrink-0" />
                <h3 className="font-semibold text-sm flex-1">{t("pmhr_links_title")}</h3>
                {pmhrLinks.some((l) => l.status === "pending") && (
                  <Badge className="text-[10px] bg-amber-500/15 text-amber-700 border-amber-500/30" variant="outline" data-testid="pmhr-links-pending-badge">
                    {pmhrLinks.filter((l) => l.status === "pending").length} {t("pmhr_link_pending").toLowerCase()}
                  </Badge>
                )}
              </div>
              <p className="text-[11px] text-muted-foreground">{t("pmhr_links_hint")}</p>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {pmhrLinks.map((l) => (
                  <div key={l.id} className="rounded-lg border px-3 py-2.5 space-y-2" data-testid={`pmhr-link-row-${l.id}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <Badge variant="secondary" className="text-[10px] shrink-0">
                        {l.kind === "crew" ? t("pmhr_kind_crew") : t("pmhr_kind_supervisor")}
                      </Badge>
                      <span className="text-sm font-medium min-w-0 truncate flex-1">{l.label}</span>
                      {l.status === "linked" ? (
                        <Badge variant="outline" className="text-[10px] text-emerald-700 border-emerald-500/40 bg-emerald-500/10 shrink-0 max-w-[50%]">
                          <span className="truncate">{t("pmhr_link_linked")}: {l.user_name || "—"}</span>
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px] text-amber-700 border-amber-500/40 bg-amber-500/10 shrink-0">
                          {t("pmhr_link_pending")}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Select value={linkPick[l.id] || ""} onValueChange={(v) => setLinkPick((p) => ({ ...p, [l.id]: v }))}>
                        <SelectTrigger className="h-9 flex-1 min-w-0 text-xs" data-testid={`pmhr-link-select-${l.id}`}>
                          <SelectValue placeholder={t("pmhr_link_select")} />
                        </SelectTrigger>
                        <SelectContent>
                          {(linkableGroups.length > 0
                            ? linkableGroups
                            : [{ company_id: "own", company_name: "", own: true, users: users.filter((u) => !u.super_admin) }]
                          ).map((g) => (
                            (g.users || []).length > 0 && (
                              <SelectGroup key={g.company_id}>
                                {g.company_name ? (
                                  <SelectLabel className="text-[11px] uppercase tracking-wide opacity-70">
                                    {g.company_name}{g.own ? "" : ` · ${t("pmhr_partner_company")}`}
                                  </SelectLabel>
                                ) : null}
                                {(g.users || []).map((u) => (
                                  <SelectItem key={u.id} value={u.id}>{u.name} — {t(u.role)}</SelectItem>
                                ))}
                              </SelectGroup>
                            )
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        size="sm" className="h-9 shrink-0"
                        data-testid={`pmhr-link-assign-${l.id}`}
                        disabled={!linkPick[l.id] || linkBusy === l.id}
                        onClick={() => assignPmhrLink(l)}
                      >
                        {linkBusy === l.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : t("pmhr_link_btn")}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
          <Card className="p-4 space-y-3" data-testid="pmhr-status-card">
            <div className="flex items-center gap-2">
              <FolderSync className="h-5 w-5 text-primary shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm">{t("pmhr_title")}</div>
                <div className="text-[11px] text-muted-foreground">
                  {pmhr ? t("pmhr_window").replace("{p}", pmhr.window_past_days).replace("{f}", pmhr.window_future_days) : "—"}
                </div>
              </div>
              <Button data-testid="pmhr-sync-now-btn" size="sm" onClick={syncNow} disabled={syncing}>
                <RefreshCw className={`h-4 w-4 mr-1.5 ${syncing ? "animate-spin" : ""}`} />
                {syncing ? t("pmhr_syncing") : t("pmhr_sync_now")}
              </Button>
            </div>
            <div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
              <div>
                <div className="text-sm font-medium">{t("pmhr_auto_sync")}</div>
                <div className="text-[11px] text-muted-foreground">
                  {pmhr?.enabled
                    ? t("pmhr_auto_on").replace("{n}", pmhr?.interval_minutes ?? 5)
                    : t("pmhr_auto_off")}
                </div>
              </div>
              <Switch
                data-testid="pmhr-auto-sync-switch"
                checked={!!pmhr?.enabled}
                onCheckedChange={toggleAutoSync}
              />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Card className="p-2.5 text-center" data-testid="pmhr-stat-jobs">
                <Briefcase className="h-4 w-4 mx-auto text-primary mb-1" />
                <div className="text-lg font-bold font-display leading-none">{pmhr?.synced_jobs ?? "—"}</div>
                <div className="text-[10px] text-muted-foreground mt-1">{t("pmhr_synced_jobs")}</div>
              </Card>
              <Card className="p-2.5 text-center" data-testid="pmhr-stat-pdfs">
                <FileText className="h-4 w-4 mx-auto text-primary mb-1" />
                <div className="text-lg font-bold font-display leading-none">{pmhr?.synced_pdfs ?? "—"}</div>
                <div className="text-[10px] text-muted-foreground mt-1">{t("pmhr_synced_pdfs")}</div>
              </Card>
              <Card className="p-2.5 text-center" data-testid="pmhr-stat-last">
                <RefreshCw className="h-4 w-4 mx-auto text-primary mb-1" />
                <div className="text-[11px] font-semibold leading-tight">{fmtDate(pmhr?.last_sync_at)}</div>
                <div className="text-[10px] text-muted-foreground mt-1">{t("pmhr_last_sync")}</div>
              </Card>
            </div>
            {pmhr?.last_result && (
              <div className="flex items-center gap-1.5 flex-wrap" data-testid="pmhr-last-result">
                {pmhr.last_result.ok
                  ? <CheckCircle2 className="h-4 w-4 text-primary" />
                  : <AlertTriangle className="h-4 w-4 text-destructive" />}
                <Badge variant="secondary" className="text-[10px]">{t("pmhr_created")}: {pmhr.last_result.created}</Badge>
                <Badge variant="secondary" className="text-[10px]">{t("pmhr_updated")}: {pmhr.last_result.updated}</Badge>
                <Badge variant="secondary" className="text-[10px]">{t("pmhr_deleted")}: {pmhr.last_result.deleted}</Badge>
                <Badge variant="secondary" className="text-[10px]">{t("pmhr_pdfs")}: {pmhr.last_result.pdfs}</Badge>
                <Badge variant="secondary" className="text-[10px]">{t("pmhr_skipped")}: {pmhr.last_result.skipped}</Badge>
                {(pmhr.last_result.errors?.length || 0) > 0 && (
                  <Badge variant="destructive" className="text-[10px]">{t("pmhr_errors")}: {pmhr.last_result.errors.length}</Badge>
                )}
              </div>
            )}
            <p className="text-[11px] text-muted-foreground leading-relaxed">{t("pmhr_note")}</p>
          </Card>

          {/* MASTER ONLY: global duplicate scan/cleanup (crosses companies) */}
          {isMaster && (
          <Card className="p-4 space-y-2.5" data-testid="pmhr-duplicates-card">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-primary shrink-0" />
              <div className="font-semibold text-sm flex-1">{t("dup_title")}</div>
              <Button data-testid="pmhr-dup-check-btn" size="sm" variant="outline" onClick={checkDuplicates} disabled={dupBusy}>
                <RefreshCw className={`h-4 w-4 mr-1.5 ${dupBusy ? "animate-spin" : ""}`} />
                {dupBusy ? t("dup_checking") : t("dup_check")}
              </Button>
            </div>
            {dupResult && (
              dupResult.extra_job_docs === 0 && dupResult.extra_pdfs === 0 ? (
                <div className="flex items-center gap-2 text-sm text-primary" data-testid="pmhr-dup-clean">
                  <CheckCircle2 className="h-4 w-4" /> {t("dup_none")}
                </div>
              ) : (
                <div className="space-y-2" data-testid="pmhr-dup-found">
                  <p className="text-sm text-destructive font-medium">
                    {t("dup_found")
                      .replace("{j}", dupResult.job_groups.length)
                      .replace("{e}", dupResult.extra_job_docs)
                      .replace("{p}", dupResult.extra_pdfs)}
                  </p>
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {dupResult.job_groups.map((g) => (
                      <div key={g.id} className="flex items-center gap-2 text-xs border-b border-border/60 pb-1 last:border-0" data-testid="pmhr-dup-row">
                        <Badge variant="destructive" className="text-[9px] px-1.5">{g.count}x</Badge>
                        <span className="font-mono-plex font-semibold">#{g.job_number}</span>
                        <span className="text-muted-foreground truncate">{g.customer}</span>
                      </div>
                    ))}
                  </div>
                  <Button data-testid="pmhr-dup-delete-btn" size="sm" variant="destructive" onClick={deleteDuplicates} disabled={dupBusy}>
                    <Trash2 className="h-4 w-4 mr-1.5" /> {t("dup_delete_all")}
                  </Button>
                </div>
              )
            )}
          </Card>
          )}

          {/* Sync source configuration — each company points to ITS OWN system */}
          <Card className="p-4 space-y-3" data-testid="pmhr-config-card">
            <div className="font-semibold text-sm">{t("sync_source_title")}</div>
            <p className="text-[11px] text-muted-foreground -mt-2">{t("sync_source_sub")}</p>
            <Input
              data-testid="pmhr-config-url"
              placeholder={t("sync_base_url")}
              value={syncCfg.base_url}
              onChange={(e) => setSyncCfg((s) => ({ ...s, base_url: e.target.value }))}
              className="h-10 text-sm"
            />
            <div className="grid grid-cols-2 gap-2">
              <Input
                data-testid="pmhr-config-username"
                placeholder={t("sync_username")}
                value={syncCfg.username}
                onChange={(e) => setSyncCfg((s) => ({ ...s, username: e.target.value }))}
                className="h-10 text-sm"
              />
              <Input
                data-testid="pmhr-config-password"
                type="password"
                placeholder={pmhr?.password_set ? t("sync_password_saved") : t("sync_password")}
                value={syncCfg.password}
                onChange={(e) => setSyncCfg((s) => ({ ...s, password: e.target.value }))}
                className="h-10 text-sm"
              />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-[10px] text-muted-foreground">{t("sync_interval")}</label>
                <Input data-testid="pmhr-config-interval" type="number" min={2} max={120} value={syncCfg.interval_minutes}
                  onChange={(e) => setSyncCfg((s) => ({ ...s, interval_minutes: e.target.value }))} className="h-9 text-sm" />
              </div>
              <div>
                <label className="text-[10px] text-muted-foreground">{t("sync_past_days")}</label>
                <Input data-testid="pmhr-config-past" type="number" min={0} max={365} value={syncCfg.window_past_days}
                  onChange={(e) => setSyncCfg((s) => ({ ...s, window_past_days: e.target.value }))} className="h-9 text-sm" />
              </div>
              <div>
                <label className="text-[10px] text-muted-foreground">{t("sync_future_days")}</label>
                <Input data-testid="pmhr-config-future" type="number" min={0} max={365} value={syncCfg.window_future_days}
                  onChange={(e) => setSyncCfg((s) => ({ ...s, window_future_days: e.target.value }))} className="h-9 text-sm" />
              </div>
            </div>
            {/* Default supervisor for SERVICE jobs (matriz never links one) */}
            <div>
              <label className="text-[10px] text-muted-foreground">{t("service_supervisor_label")}</label>
              <Select
                value={syncCfg.service_supervisor_id || "none"}
                onValueChange={(v) => setSyncCfg((s) => ({ ...s, service_supervisor_id: v === "none" ? "" : v }))}
              >
                <SelectTrigger className="h-9 text-sm" data-testid="pmhr-service-supervisor">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t("service_supervisor_none")}</SelectItem>
                  {linkableGroups.map((g) => (g.users || []).map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.name} — {g.company_name}{u.role ? ` (${t(u.role)})` : ""}
                    </SelectItem>
                  )))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-muted-foreground mt-1">{t("service_supervisor_hint")}</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button data-testid="pmhr-config-save-btn" size="sm" onClick={saveSyncCfg} disabled={savingSyncCfg}>
                {savingSyncCfg ? <RefreshCw className="h-4 w-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />}
                {t("sync_save")}
              </Button>
              <Button data-testid="pmhr-test-btn" size="sm" variant="outline" onClick={testSyncConnection} disabled={testingSync}>
                {testingSync ? <RefreshCw className="h-4 w-4 mr-1.5 animate-spin" /> : <Zap className="h-4 w-4 mr-1.5" />}
                {t("sync_test")}
              </Button>
            </div>
            {syncTest && (
              syncTest.ok ? (
                <div className="flex items-start gap-2 rounded-lg border border-[#166534]/30 bg-[#166534]/5 px-3 py-2.5" data-testid="pmhr-test-ok">
                  <CheckCircle2 className="h-4 w-4 text-[#166534] mt-0.5 shrink-0" />
                  <p className="text-xs text-[#166534] font-medium">
                    {t("sync_test_ok").replace("{n}", syncTest.jobs_found)}
                  </p>
                </div>
              ) : (
                <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2.5" data-testid="pmhr-test-fail">
                  <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
                  <div className="text-xs space-y-1">
                    <p className="font-semibold text-destructive">{syncErrorText(syncTest.error_code)}</p>
                    {syncTest.detail ? <p className="text-muted-foreground break-all">({syncTest.detail})</p> : null}
                  </div>
                </div>
              )
            )}
            {/* Last automatic/manual sync error, explained */}
            {pmhr?.last_result && !pmhr.last_result.ok && (
              <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2.5" data-testid="pmhr-last-error">
                <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                <div className="text-xs space-y-0.5">
                  <p className="font-semibold text-amber-700">{t("sync_last_error_title")}</p>
                  <p className="text-muted-foreground">
                    {pmhr.last_result.error_code ? syncErrorText(pmhr.last_result.error_code) : (pmhr.last_result.errors || []).slice(0, 2).join("; ")}
                  </p>
                </div>
              </div>
            )}
          </Card>

          <Card className="p-4" data-testid="pmhr-logs-card">
            <div className="font-semibold text-sm mb-2">{t("pmhr_history")}</div>
            {pmhrLogs.length === 0 ? (
              <p className="text-xs text-muted-foreground" data-testid="pmhr-no-logs">{t("pmhr_no_logs")}</p>
            ) : (
              <div className="space-y-1.5">
                {pmhrLogs.map((lg) => (
                  <div key={lg.id} className="flex items-center gap-2 text-xs border-b border-border/60 pb-1.5 last:border-0" data-testid="pmhr-log-row">
                    {lg.ok
                      ? <CheckCircle2 className="h-3.5 w-3.5 text-primary shrink-0" />
                      : <AlertTriangle className="h-3.5 w-3.5 text-destructive shrink-0" />}
                    <span className="text-muted-foreground shrink-0">{fmtDate(lg.at)}</span>
                    <Badge variant="outline" className="text-[9px] px-1">{lg.trigger}</Badge>
                    <span className="text-muted-foreground truncate">
                      +{lg.created} ~{lg.updated} -{lg.deleted} · {lg.pdfs} PDF · {Math.round((lg.duration_ms || 0) / 1000)}s
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        {/* ---------------- PUSH TOKENS (MASTER ONLY) ---------------- */}
        {isMaster && (
        <TabsContent value="push" className="mt-4 space-y-2">
          <BroadcastPanel t={t} accounts={allAccounts} />
          <InvitesPanel t={t} />
          {tokens.length === 0 ? (
            <Card className="p-6 text-center text-sm text-muted-foreground" data-testid="admin-no-tokens">
              {t("no_devices_yet")}
            </Card>
          ) : tokens.map((tk) => (
            <Card key={tk.id} className="p-3 flex items-center gap-3" data-testid="admin-token-card">
              <Smartphone className="h-5 w-5 text-primary shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">{tk.user_name} <Badge variant="secondary" className="text-[10px] ml-1">{tk.platform}</Badge></div>
                <div className="text-[11px] text-muted-foreground font-mono truncate">{tk.token_preview}</div>
              </div>
              <Button size="sm" variant="outline" className="h-8" data-testid="admin-token-test" onClick={() => testPush(tk.user_id)}>
                <Send className="h-3.5 w-3.5" />
              </Button>
              <Button size="sm" variant="outline" className="h-8 text-destructive" data-testid="admin-token-delete" onClick={() => deleteToken(tk)}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </Card>
          ))}
        </TabsContent>
        )}

        {/* ---------------- SYSTEM ---------------- */}
        {/* ---------------- SYSTEM (MASTER ONLY) ---------------- */}
        {isMaster && (
        <TabsContent value="system" className="mt-4 space-y-3">
          {stats && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {[
                [t("users"), stats.users], [t("conversations"), stats.conversations],
                [t("messages"), stats.messages], [t("media"), stats.media],
                ["Issues", stats.issues], ["Tasks", stats.tasks],
                [t("devices"), stats.device_tokens], ["APNs", stats.apns_ready ? "OK" : "—"],
              ].map(([label, value]) => (
                <Card key={label} className="p-3 text-center" data-testid="admin-stat-card">
                  <div className="text-xl font-bold font-display">{String(value)}</div>
                  <div className="text-[11px] text-muted-foreground">{label}</div>
                </Card>
              ))}
            </div>
          )}
          <Card className="p-4 space-y-2" data-testid="admin-orphans-card">
            <div className="flex items-center gap-2 font-semibold text-sm">
              <Trash2 className="h-4 w-4 text-destructive" /> {t("cleanup_orphans_title")}
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">{t("cleanup_orphans_desc")}</p>
            <Button data-testid="admin-orphans-btn" size="sm" variant="outline" className="text-destructive hover:text-destructive" onClick={() => setOrphanConfirm(true)} disabled={orphanBusy}>
              {orphanBusy ? <RefreshCw className="h-4 w-4 mr-1.5 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1.5" />}
              {t("cleanup_orphans_btn")}
            </Button>
          </Card>
          <Card className="p-4 space-y-2" data-testid="admin-download-source">
            <div className="flex items-center gap-2 font-semibold text-sm">
              <Download className="h-4 w-4 text-primary" /> {t("download_source_zip")}
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">{t("download_source_desc")}</p>
            <Button data-testid="admin-download-source-btn" size="sm" onClick={downloadSource} disabled={zipBusy}>
              <Download className={`h-4 w-4 mr-1.5 ${zipBusy ? "animate-bounce" : ""}`} />
              {zipBusy ? t("preparing_zip") : t("download_source_zip")}
            </Button>
            {zipBusy && (
              <div className="space-y-1.5 pt-1" data-testid="zip-progress-wrap">
                <div className="flex items-center justify-between text-xs font-medium">
                  <span data-testid="zip-progress-phase">
                    {zipPhase === "zipping" ? t("zip_phase_zipping") : t("zip_phase_downloading")}
                  </span>
                  <span data-testid="zip-progress-pct">{zipPct}%</span>
                </div>
                <Progress value={zipPct} className="h-2" data-testid="zip-progress-bar" />
                {zipInfo && (
                  <p className="text-[11px] text-muted-foreground" data-testid="zip-progress-info">{zipInfo}</p>
                )}
              </div>
            )}
          </Card>
          <Card className="p-4 space-y-2" data-testid="admin-seed-demo">
            <div className="flex items-center gap-2 font-semibold text-sm">
              <UserPlus className="h-4 w-4 text-primary" /> {t("seed_demo_title")}
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">{t("seed_demo_desc")}</p>
            <Button data-testid="admin-seed-demo-btn" size="sm" onClick={seedDemo} disabled={seedBusy}>
              <UserPlus className={`h-4 w-4 mr-1.5 ${seedBusy ? "animate-pulse" : ""}`} />
              {seedBusy ? "..." : t("seed_demo_title")}
            </Button>
          </Card>
          <Card className="p-4 space-y-2 border-destructive/40" data-testid="admin-cleanup-demo">
            <div className="flex items-center gap-2 font-semibold text-sm text-destructive">
              <Trash2 className="h-4 w-4" /> {t("cleanup_demo_title")}
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">{t("cleanup_demo_desc")}</p>
            <Button data-testid="admin-cleanup-demo-btn" size="sm" variant="outline" className="text-destructive hover:text-destructive border-destructive/40" onClick={() => setCleanupOpen(true)}>
              <Trash2 className="h-4 w-4 mr-1.5" /> {t("cleanup_demo_title")}
            </Button>
          </Card>
          <Card className="p-4 space-y-2" data-testid="admin-download-info">
            <div className="flex items-center gap-2 font-semibold text-sm">
              <Download className="h-4 w-4 text-primary" /> {t("source_code_title")}
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">{t("source_code_info")}</p>
            <ol className="text-xs text-muted-foreground list-decimal ml-4 space-y-1">
              <li>{t("source_step_1")}</li>
              <li>{t("source_step_2")}</li>
              <li>{t("source_step_3")}</li>
            </ol>
          </Card>
        </TabsContent>
        )}
      </Tabs>

      <EditUserDialog user={editUser} crews={crews} onClose={() => setEditUser(null)} onDone={() => { setEditUser(null); load(); }} t={t} />
      <CreateUserDialog open={createOpen} onClose={() => setCreateOpen(false)} onDone={() => { setCreateOpen(false); load(); }} t={t} />

      {/* Add an EXISTING account to my company (by e-mail or phone) */}
      <InviteDialog identifier={inviteTarget} kind="company" onClose={() => { setInviteTarget(null); setAddIdentifier(""); }} />
      <Dialog open={addOpen} onOpenChange={(v) => { if (!v) setAddOpen(false); }}>
        <DialogContent className="max-w-sm" data-testid="admin-add-user-dialog">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2">
              <UserCheck className="h-4 w-4 text-primary" /> {t("add_existing_user")}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={addExistingUser} className="space-y-3">
            <p className="text-[11px] text-muted-foreground leading-relaxed">{t("add_existing_user_hint")}</p>
            <Input
              data-testid="admin-add-user-identifier"
              placeholder={t("enter_someones_code")}
              value={addIdentifier}
              onChange={(e) => setAddIdentifier(e.target.value)}
              autoFocus
            />
            <Select value={addRole} onValueChange={setAddRole}>
              <SelectTrigger className="h-10" data-testid="admin-add-user-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROLES.map((r) => <SelectItem key={r} value={r}>{t(r)}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button type="submit" data-testid="admin-add-user-submit" className="w-full" disabled={addBusy || !addIdentifier.trim()}>
              {addBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UserCheck className="h-4 w-4 mr-2" />} {t("add_existing_user")}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Remove-from-company confirmation modal */}
      {/* MASTER: orphan cleanup confirmation */}
      <AlertDialog open={orphanConfirm} onOpenChange={setOrphanConfirm}>
        <AlertDialogContent data-testid="orphans-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("cleanup_orphans_title")}</AlertDialogTitle>
            <AlertDialogDescription>{t("cleanup_orphans_confirm")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="orphans-cancel">{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction data-testid="orphans-confirm-btn" className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={cleanupOrphans}>
              {t("cleanup_orphans_btn")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={!!removeTarget} onOpenChange={(o) => !o && setRemoveTarget(null)}>
        <AlertDialogContent data-testid="admin-remove-member-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("remove_from_company")}: {removeTarget?.name}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("remove_from_company_confirm").replace("{name}", removeTarget?.name || "")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-remove-member-cancel">{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="admin-remove-member-confirm"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={removeMember}
            >
              {t("remove_from_company")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!resetUser} onOpenChange={(o) => !o && setResetUser(null)}>
        <AlertDialogContent data-testid="factory-reset-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("factory_reset")}: {resetUser?.name}</AlertDialogTitle>
            <AlertDialogDescription>{resetUser?.email}<br />{t("factory_reset_confirm")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="factory-reset-cancel">{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction data-testid="factory-reset-confirm" disabled={resetBusy} onClick={(e) => { e.preventDefault(); factoryReset(); }} className="bg-amber-600 hover:bg-amber-700">
              {resetBusy ? "..." : t("factory_reset")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={!!deleteUser} onOpenChange={(o) => !o && setDeleteUser(null)}>
        <AlertDialogContent data-testid="admin-delete-user-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("delete_user")}: {deleteUser?.name}</AlertDialogTitle>
            <AlertDialogDescription>{deleteUser?.email}<br />{t("delete_user_confirm")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-delete-user-cancel">{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction data-testid="admin-delete-user-confirm" className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={confirmDeleteUser}>
              {t("delete_user")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={cleanupOpen} onOpenChange={setCleanupOpen}>
        <AlertDialogContent data-testid="admin-cleanup-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("cleanup_demo_confirm")}</AlertDialogTitle>
            <AlertDialogDescription>{t("cleanup_demo_desc")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-cleanup-cancel">{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction data-testid="admin-cleanup-confirm" className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={cleanupDemo}>
              {t("cleanup_demo_title")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
